import React, { useState, useEffect, useMemo } from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "../../context/AuthProvider";
import { decryptData } from "../../utils/cryptoUtils";
import dayjs from "dayjs";
import {
  Box,
  Typography,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Grid2,
} from "@mui/material";
import { DatePicker as AntDatePicker } from "antd";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {
  getTLAttendanceSummary,
  getTLEmployeeDetail,
} from "../../services/attendanceReport.service";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";
import { isAdminRole } from "../../utils/roles";

const { RangePicker } = AntDatePicker;

// ── Helpers ───────────────────────────────────────────────────────────────────

// Work  = trackedDurationSec (desktop timer total — authoritative)
// Break = (clockOut − clockIn) − trackedDurationSec
function calcBreak(clockInAt, clockOutAt, trackedDurationSec) {
  if (!clockInAt || !clockOutAt) return 0;
  const spanSec = (new Date(clockOutAt) - new Date(clockInAt)) / 1000;
  return Math.max(0, spanSec - (trackedDurationSec || 0));
}

function fmtSec(sec) {
  if (!sec || sec <= 0) return "—";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function fmtTime(dt) {
  if (!dt) return "—";
  return dayjs(dt).format("hh:mm A");
}

function fmtDate(dt) {
  if (!dt) return "—";
  return dayjs(dt).format("DD MMM YYYY");
}

function fmtWeekday(dt) {
  if (!dt) return "";
  return dayjs(dt).format("ddd");
}

const STATUS_COLOR = {
  present: "success",
  late: "warning",
  half_day_absent: "warning",
  half_day: "warning",
  absent: "error",
  leave: "info",
  holiday: "info",
  weekend: "default",
};

// ── Summary View (all users for a date) ───────────────────────────────────────

function SummaryView({ onUserSelect }) {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const fetchData = async (date) => {
    setLoading(true);
    try {
      const formatted = dayjs(date).format("YYYY-MM-DD");
      const res = await getTLAttendanceSummary({ startDate: formatted, endDate: formatted });
      const rows = res?.data || [];
      setData(
        rows.map((r) => ({
          ...r,
          _clockIn:    r.clockInAt  ? fmtTime(r.clockInAt)  : "—",
          _clockOut:   r.clockOutAt ? fmtTime(r.clockOutAt) : "—",
          _workTime:   fmtSec(r.trackedDurationSec),
          _break:      fmtSec(calcBreak(r.clockInAt, r.clockOutAt, r.trackedDurationSec)),
          _statusChip: r.status || "—",
        }))
      );
    } catch (e) {
      console.error("Attendance summary error:", e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(selectedDate); }, [selectedDate]);

  const filtered = (() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((r) =>
      (r.userName || r.name || "").toLowerCase().includes(q) ||
      (r.email || "").toLowerCase().includes(q)
    );
  })();

  const columns = [
    { id: "userName",  label: "Name" },
    { id: "_clockIn",  label: "Clock In" },
    { id: "_clockOut", label: "Clock Out" },
    { id: "_workTime", label: "Work Time" },
    { id: "_break",    label: "Break" },
    {
      id: "_statusChip",
      label: "Status",
      render: (val) => (
        <Chip
          label={(val || "—").replace(/_/g, " ")}
          color={STATUS_COLOR[val] || "default"}
          size="small"
          sx={{ textTransform: "capitalize", fontSize: 11 }}
        />
      ),
    },
  ];

  const tableConfig = {
    isAction: false, isView: true, isEdit: false, isDelete: false,
    isToggle: false, isApprove: false, isReject: false,
    isPdfView: false, isMore: false, isHistory: false,
    isAvailableLeave: false, isAdmin: false, isClient: false,
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Single row: Search (left) + Date picker (right) */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
        <Stack direction="row" alignItems="center" gap={1}>
          <TextField
            size="small"
            placeholder="Search by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: { xs: 200, sm: 280 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")} title="Clear">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {search && (
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
              {filtered.length} of {data.length}
            </Typography>
          )}
        </Stack>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]} sx={{ pt: 0 }}>
            <DatePicker
              value={selectedDate}
              format="DD/MM/YYYY"
              onChange={(d) => setSelectedDate(d)}
              className="datePicker"
              slotProps={{ textField: { size: "small" } }}
            />
          </DemoContainer>
        </LocalizationProvider>
      </Stack>

      <MasterCommonTable
        columns={columns}
        data={filtered}
        handleView={(row) => onUserSelect(row.userId || row._id, row.userName || row.name)}
        tableConfig={tableConfig}
        openLoader={loading}
        isServerSide={false}
        isPaginationBar={false}
      />
    </Box>
  );
}

// ── Detail View (single user monthly records) ─────────────────────────────────

function DetailView({ userId, userName, onBack }) {
  const defaultFrom = dayjs().startOf("month");
  const defaultTo   = dayjs();

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo,   setDateTo]   = useState(defaultTo);
  const [data,    setData]    = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchDetail = async (from, to) => {
    if (!from || !to) return;
    setLoading(true);
    try {
      const startDate = dayjs(from).format("YYYY-MM-DD");
      const endDate   = dayjs(to).format("YYYY-MM-DD");
      const res = await getTLEmployeeDetail(userId, { startDate, endDate });
      const records = res?.data?.records || res?.data || [];
      const summ    = res?.data?.summary || null;
      setSummary(summ);

      // Group multiple sessions on the same day into one row
      const grouped = {};
      for (const r of records) {
        const key = r.date ? String(r.date).slice(0, 10) : String(r._id);
        if (!grouped[key]) {
          grouped[key] = { _id: key, date: r.date, sessions: [], status: r.status, totalTrackedSec: 0, totalBreakSec: 0 };
        }
        grouped[key].sessions.push(r);
        grouped[key].totalTrackedSec += r.trackedDurationSec || 0;
        grouped[key].totalBreakSec   += calcBreak(r.clockInAt, r.clockOutAt, r.trackedDurationSec);
        grouped[key].status = r.status;
      }

      setData(
        Object.values(grouped)
          .sort((a, b) => new Date(b.date) - new Date(a.date))
          .map((row) => ({
            ...row,
            _date:      fmtDate(row.date),
            _day:       fmtWeekday(row.date),
            _clockIn:   row.sessions.map((s) => fmtTime(s.clockInAt)).join(" · "),
            _clockOut:  row.sessions.map((s) => fmtTime(s.clockOutAt)).join(" · "),
            _workTime:  fmtSec(row.totalTrackedSec),
            _break:     fmtSec(row.totalBreakSec),
            _statusVal: row.status,
          }))
      );
    } catch (e) {
      console.error("Attendance detail error:", e);
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (userId) fetchDetail(dateFrom, dateTo); }, [userId]);

  const handleFromChange = (val) => {
    setDateFrom(val);
    if (val && dateTo) fetchDetail(val, dateTo);
  };

  const handleToChange = (val) => {
    setDateTo(val);
    if (dateFrom && val) fetchDetail(dateFrom, val);
  };

  const handleClear = () => {
    const from = defaultFrom;
    const to   = defaultTo;
    setDateFrom(from);
    setDateTo(to);
    fetchDetail(from, to);
  };

  const columns = [
    {
      id: "_date",
      label: "Date",
      render: (val, row) => (
        <Box>
          <Typography variant="body2" fontWeight={600}>{val}</Typography>
          <Typography variant="caption" color="text.secondary">{row._day}</Typography>
        </Box>
      ),
    },
    { id: "_clockIn",  label: "Clock In" },
    { id: "_clockOut", label: "Clock Out" },
    { id: "_workTime", label: "Work Time" },
    { id: "_break",    label: "Break" },
    {
      id: "_statusVal",
      label: "Status",
      render: (val) => (
        <Chip
          label={(val || "—").replace(/_/g, " ")}
          color={STATUS_COLOR[val] || "default"}
          size="small"
          sx={{ textTransform: "capitalize", fontSize: 11 }}
        />
      ),
    },
  ];

  const tableConfig = {
    isAction: false, isView: false, isEdit: false, isDelete: false,
    isToggle: false, isApprove: false, isReject: false,
    isPdfView: false, isMore: false, isHistory: false,
    isAvailableLeave: false, isAdmin: false, isClient: false,
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header + filter row — single line */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2} gap={2}>
        {/* Left: back button (admin only) + title */}
        <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
          {onBack && (
            <IconButton size="small" onClick={onBack} sx={{ color: "#008080", flexShrink: 0 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" fontWeight="bold" noWrap>
            {userName} — Attendance
          </Typography>
        </Stack>

        {/* Right: date range picker — fixed width, no stretch */}
        <Box sx={{ flexShrink: 0 }}>
          <RangePicker
            value={[dateFrom, dateTo]}
            onChange={(dates) => {
              if (!dates) { handleClear(); return; }
              const [from, to] = dates;
              setDateFrom(from);
              setDateTo(to);
              if (from && to) fetchDetail(from, to);
            }}
            format="DD/MM/YYYY"
            className="datePicker"
            allowClear
            style={{ width: 250 }}
          />
        </Box>
      </Stack>

      {/* Summary chips */}
      {summary && (
        <Stack direction="row" flexWrap="wrap" gap={1} mb={2}>
          <Chip
            icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
            label={`Total Work: ${fmtSec(summary.totalTrackedSec)}`}
            color="primary"
            variant="outlined"
            size="small"
          />
          {Object.entries(summary.byStatus || {}).map(([k, v]) => (
            <Chip
              key={k}
              label={`${k.replace(/_/g, " ")}: ${v}d`}
              color={STATUS_COLOR[k] || "default"}
              variant="outlined"
              size="small"
              sx={{ textTransform: "capitalize" }}
            />
          ))}
        </Stack>
      )}

      <MasterCommonTable
        columns={columns}
        data={data}
        tableConfig={tableConfig}
        openLoader={loading}
        isServerSide={false}
        isPaginationBar={false}
      />
    </Box>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

const TimeLoggerAttendance = () => {
  const { accessToken } = useAuth();
  const [selectedUserId,   setSelectedUserId]   = useState(null);
  const [selectedUserName, setSelectedUserName] = useState("");

  // Decode token once
  const decoded = useMemo(() => {
    if (!accessToken) return null;
    try { return jwtDecode(accessToken); } catch { return null; }
  }, [accessToken]);

  const isAdmin  = isAdminRole(decoded?.role);
  const myUserId = decoded?.id || null;
  const myName   = decoded?.name ? (decryptData(decoded.name) || "My Attendance") : "My Attendance";

  const handleSelect = (userId, userName) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
  };

  const handleBack = () => {
    setSelectedUserId(null);
    setSelectedUserName("");
  };

  // Regular user → show only their own attendance directly
  if (!isAdmin) {
    return (
      <DetailView
        userId={myUserId}
        userName={myName}
        onBack={null}
      />
    );
  }

  // Admin → summary list → click user → detail
  if (selectedUserId) {
    return (
      <DetailView
        userId={selectedUserId}
        userName={selectedUserName}
        onBack={handleBack}
      />
    );
  }
  return <SummaryView onUserSelect={handleSelect} />;
};

export default TimeLoggerAttendance;
